/**
 * @name setScrollTrigger
 */
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);
gsap.config({ nullTargetWarn: false, });

/**
 * @name setScrollTrigger
 * @description 各ページのアニメーションを呼び出す
 */
export function setScrollTrigger() {
    setScrollTriggerAction();
    setScrollTriggerAction__Top();
};

/**
 * 初期値
 */
let mm = gsap.matchMedia();
let props = {
    action: { duration: 1.2, ease: 'expo.Out', overwrite: true, },
    scrub: { scrub: 1.5, invalidateOnRefresh: true },
    trigger: { start: `top 70%`, end: `top 70%`, once: true },
    stalker: { duration: 0.3, ease: 'expo.Out', overwrite: false, },
    scrubParallaxItem: {
        start: `top bottom`,
        end: 'bottom top',
        scrub: 1.5,
        invalidateOnRefresh: true,
    },
    scrubParallaxBg: {
        start: `top 80%`,
        end: '80% top',
        scrub: 1.5,
        invalidateOnRefresh: true,
    },
    scrubBg: {
        start: `center top`,
        end: '100% top',
        scrub: 1.5,
        invalidateOnRefresh: true,
    }
}

/**
 * @name setScrollTriggerInit
 * @description 共通アニメーション
 * @param {string} getTrigger - トリガー要素
 * @param {string} type - アニメーションタイプ
 */
function setScrollTriggerInit(getTrigger, type = '') {

    // x軸移動
    const moveX = () => {
        let moveXItem = getTrigger.querySelectorAll('.--js_moveX__item');
        if (moveXItem.length === 0) return;
        ScrollTrigger.batch(getTrigger, {
            onEnter: () => gsap.to([moveXItem], { ...props.action, x: '0%', opacity: 1, stagger: 0.1, }),
            ...props.trigger,
        });
    }

    // y軸移動
    const moveY = () => {
        let moveYItem = getTrigger.querySelectorAll('.--js_moveY__item');
        if (moveYItem.length === 0) return;
        ScrollTrigger.batch(getTrigger, {
            onEnter: () => gsap.to([moveYItem], { ...props.action, y: '0%', opacity: 1, stagger: 0.1, }),
            ...props.trigger,
        });
    }

    // スケールアップ
    const scaleUp = () => {
        let scaleUpItem = getTrigger.querySelectorAll('.--js_scaleUp__item');
        if (scaleUpItem.length === 0) return;
        gsap.set(scaleUpItem, { scale: 0, });
        ScrollTrigger.batch(getTrigger, {
            onEnter: () => gsap.to([scaleUpItem], { ...props.action, scale: 1, stagger: 0.1, }),
            ...props.trigger,
        });
    }

    // アニメーション再生・停止
    const animationPlayState = () => {
        let playState = getTrigger.querySelectorAll('.--js_animation__playState');
        if (playState.length === 0) return;
        ScrollTrigger.batch(getTrigger, {
            onEnter: () => gsap.to([playState], { animationPlayState: 'running', }),
            onLeave: () => gsap.to([playState], { animationPlayState: 'paused', }),
            onEnterBack: () => gsap.to([playState], { animationPlayState: 'running', }),
            onLeaveBack: () => gsap.to([playState], { animationPlayState: 'paused', }),
            start: `top bottom`,
            end: `bottom top`,
        });
    }

    // パララックス - 背景
    const parallaxElBg = () => {
        let parallaxTriggers = getTrigger.querySelectorAll('.--js_parallax__item__wrap');
        if (parallaxTriggers.length == 0) return;

        const parallaxEffect = (parallaxTrigger) => {
            let triggerH = parallaxTrigger.getBoundingClientRect().height;
            let parallaxItem = parallaxTrigger.querySelector('img');
            let parallaxItemH = parallaxItem.clientHeight * 1.1;
            gsap.to(parallaxItem, { y: `${-(parallaxItemH - triggerH)}px`, ease: "none", scrollTrigger: { trigger: parallaxTrigger, ...props.scrubParallaxBg }, })
        };

        parallaxTriggers.forEach((parallaxTrigger) => parallaxEffect(parallaxTrigger));
    }

    // パララックス - 要素を下から上に移動
    const parallaxElBottom = () => {
        let parallaxItems = getTrigger.querySelectorAll('.--js_parallax__item__bottom');
        if (parallaxItems.length == 0) return;

        const parallaxEffect = (parallaxItem) => {
            let itemH = parallaxItem.getBoundingClientRect().height;
            gsap.set(parallaxItem, { y: `${itemH * 0.25}px` })
            gsap.to(parallaxItem, { y: `-${itemH * 0.25}px`, ease: "none", scrollTrigger: { trigger: parallaxItem, ...props.scrubParallaxItem }, })
        };

        parallaxItems.forEach((parallaxItem) => parallaxEffect(parallaxItem));
    }

    // パララックス - 左右どちらかの余白分だけ移動
    const parallaxElSide = () => {
        let parallaxItems = getTrigger.querySelectorAll('.--js_parallax__item__side');
        if (parallaxItems.length == 0) return;

        const setPos = (parallaxItem) => {
            let itemPos = parallaxItem.getBoundingClientRect();
            let parentW = parallaxItem.parentElement.getBoundingClientRect();
            let left = itemPos.left - parentW.left;
            let right = parentW.right - itemPos.right;
            let xPos = left > right ? ((left / 2 - 10) * -1) : left === right ? 0 : ((right / 2 - 10) * 1);
            return xPos;
        };

        const parallaxEffect = (parallaxItem) => {
            let xPos = setPos(parallaxItem);
            gsap.to(parallaxItem, { x: `${xPos}px`, ease: "none", scrollTrigger: { trigger: getTrigger, ...props.scrubParallaxItem }, })
        };

        parallaxItems.forEach((parallaxItem) => parallaxEffect(parallaxItem));
    }

    // 背景色の変更 - opacity 0 ~ 1
    const changeBgColor = () => {
        let bgToggle = getTrigger.querySelector('.--js_bg__toggle');
        if (!bgToggle) return;
        gsap.to(bgToggle, { opacity: 1, ease: "none", scrollTrigger: { trigger: bgToggle, ...props.scrubBg } })
    }

    // アニメーションのオプション
    type == 'moveX' && moveX();
    type == 'moveY' && moveY();
    type == 'scaleUp' && scaleUp();
    type == 'animationPlayState' && animationPlayState();
    type == 'parallaxElBg' && parallaxElBg();
    type == 'parallaxElBottom' && parallaxElBottom();
    type == 'parallaxElSide' && parallaxElSide();
    type == 'changeBgColor' && changeBgColor();
}

/**
 * @name setScrollTriggerAction
 * @description 共通ページ用アニメーション
 */
function setScrollTriggerAction() {
    let setTrigger = document.querySelectorAll('.--js_setTrigger');
    setTrigger.forEach((getTrigger) => {
        setScrollTriggerInit(getTrigger, 'moveX');
        setScrollTriggerInit(getTrigger, 'moveY');
        setScrollTriggerInit(getTrigger, 'scaleUp');
        setScrollTriggerInit(getTrigger, 'animationPlayState');
        setScrollTriggerInit(getTrigger, 'parallaxElBg');
        setScrollTriggerInit(getTrigger, 'parallaxElBottom');
        setScrollTriggerInit(getTrigger, 'changeBgColor');
        setScrollTriggerInit(getTrigger, 'parallaxElSide');
    });
}

/**
 * @name setScrollTriggerAction__Top
 * @description トップページ用アニメーション
 */
export function setScrollTriggerAction__Top() {

    //プロパティ
    const setPropaty = () => {
        return {
            topConcept: document.querySelector('.top-concept'),
            stickyImg: document.querySelector('.top-concept .stickyImg'),
            stickyImgOverlay: document.querySelector('.top-concept .stickyImg__overlay'),
            concept: document.querySelector('.top-concept .--block-concept'),
            copy: document.querySelectorAll('.top-concept .copy span span'),
            text: document.querySelectorAll('.top-concept .text p'),
            bg: document.querySelectorAll('.top-concept .bg'),
            bgOverlay: document.querySelectorAll('.top-concept .bg .bg__overlay'),
            topService: document.querySelector('.top-service'),
        }
    }

    // トップページ - コンセプト
    const setScrollTrigger__Concept = () => {
        let { topConcept, stickyImg, stickyImgOverlay, concept, copy, text, bg, bgOverlay } = setPropaty();
        if (!topConcept) return;
        let conceptRect, conceptW, conceptH;

        // 画像のサイズ取得
        let getConceptSize = () => {
            conceptRect = concept.getBoundingClientRect();
            conceptW = conceptRect.width;
            conceptH = conceptRect.height;
            return conceptW, conceptH;
        }

        // リサイズ時に画像のサイズを取得
        window.addEventListener('resize', getConceptSize);

        // スクロールアニメーション - 画像
        const transformStickyImg = () => {
            gsap.to([stickyImg, stickyImgOverlay], {
                width: () => {
                    getConceptSize();
                    return conceptRect.width;
                },
                height: () => {
                    getConceptSize();
                    return conceptRect.height;
                },
                borderRadius: 0,
                opacity: 1,
                ease: "none",
                scrollTrigger: { trigger: stickyImg, start: `center center`, end: `150% center`, ...props.scrub },
            })
        }

        // スクロールアニメーション - 背景
        const transformBg = () => {
            let scrollTriggerProps = { trigger: bg, start: `top 70%`, end: `bottom 70%`, ...props.scrub }
            // スクロールアニメーション - 背景
            gsap.to([bg], { scale: 1, opacity: 1, ease: "none", scrollTrigger: scrollTriggerProps })
            // スクロールアニメーション - オーバーレイ
            gsap.to([bgOverlay], { opacity: 1, ease: "none", scrollTrigger: scrollTriggerProps })
        }

        // スクロールアニメーション - テキスト関連
        const transformText = () => {
            let scrollTriggerProps = { trigger: topConcept, start: `20% top`, end: `center center`, ...props.scrub }
            // スクロールアニメーション - コピー
            gsap.to([copy], { yPercent: -100, rotateZ: 0, ease: "none", scrollTrigger: scrollTriggerProps, stagger: { from: "start", amount: 0.5 } })
            // スクロールアニメーション - テキスト
            gsap.to([text], { opacity: 1, ease: "none", scrollTrigger: scrollTriggerProps })
        }

        // メディアクエリによる条件分岐
        mm.add("(min-width: 835px)", () => transformStickyImg())
            .add("(max-width: 834px)", () => transformBg());
        transformText();
    }

    //トップページ - サービス
    const setScrollTrigger__Service = () => {
        window.addEventListener('mousemove', (e) => {
            let imgWrap = document.querySelectorAll('.stalker-img .img');
            let img = document.querySelectorAll('.stalker-img .img img');
            let x = e.clientX - window.innerWidth / 2;
            let y = e.clientY - window.innerHeight / 2;
            gsap.to(imgWrap, { x: x / 10, y: y / 10, rotateZ: -x / 300, ...props.stalker })
            gsap.to(img, { x: x / 25, y: y / 25, ...props.stalker })
        });
    }

    setScrollTrigger__Concept();
    setScrollTrigger__Service();
}